import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import "../css/calendar-update.css";
import AdminMenu from './adminMenu';
import EventMileageModal from './EventMileageModal';
const CalendarUpdate = ({ setIsAuthenticated }) => {
    const [showMileage, setShowMileage] = useState(false);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingEvent, setEditingEvent] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);

    // Load events
    const loadEvents = async () => {
        setLoading(true);
        try {
            const res = await fetch("/api/FacebookEvents");
            if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
            const data = await res.json();

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const filtered = Array.isArray(data)
                ? data
                    .filter(ev => ev.start_time && new Date(ev.start_time) >= today)
                    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                : [];

            setEvents(filtered);
        } catch (err) {
            console.error("Error loading events:", err);
            setEvents([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadEvents();
    }, []);

    // Convert ISO to input type="datetime-local"
    const toInputDate = iso => {
        if (!iso) return "";
        const d = new Date(iso);
        return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
    };

    const fromInputDate = value => new Date(value).toISOString();

    // Update flat or nested fields in state
    const updateField = (field, value) => {
        setEditingEvent({ ...editingEvent, [field]: value });
    };

    const updateNested = (parent, child, value) => {
        setEditingEvent({
            ...editingEvent,
            [parent]: { ...editingEvent[parent], [child]: value }
        });
    };

    // Save one event at a time
    const handleSave = async e => {
        e.preventDefault();
        setSaving(true);

        try {
            if (!editingEvent) throw new Error("No event is being edited");

            // Build payload with keys matching backend
            const payload = {
                id: editingEvent.id,
                name: editingEvent.name,
                description: editingEvent.description || "",
                start_time: editingEvent.start_time, // required
                end_time: editingEvent.end_time || null,
                type: editingEvent.type, // required
                timezone: editingEvent.timezone || "UTC",
                ticket_uri: editingEvent.ticket_uri || "",
                category: editingEvent.category || null,
                is_canceled: editingEvent.is_canceled ?? false,
                scheduled_publish_time: editingEvent.scheduled_publish_time || null,
                place: {
                    name: editingEvent.place?.name || "Unknown Place"
                },
                cover: {
                    id: editingEvent.cover?.id || `cover-${editingEvent.id}`,
                    source: editingEvent.cover?.source || "https://via.placeholder.com/400x200.png?text=No+Cover"
                }
            };

            const res = await fetch("/api/FacebookEvents/update", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const errText = await res.text();
                throw new Error(`Save failed: ${errText}`);
            }

            alert("Event updated!");
            setEditingEvent(null);
            setShowModal(false);
            loadEvents();
        } catch (err) {
            console.error(err);
            alert(err.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <p>Loading events…</p>;

    return (
        <div className="admin-container">
            <AdminMenu setIsAuthenticated={setIsAuthenticated} />
            <div className="calendar-update-container">
                <h1>Calendar Update</h1>

                <button type="button" onClick={() => setShowMileage(true)}>
                    Mileage (taxes)
                </button>

                {showMileage && <EventMileageModal onClose={() => setShowMileage(false)} />}

                <table className="calendar-update-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Start</th>
                            <th>Location</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {events.map(ev => (
                            <tr key={ev.id}>
                                <td>{ev.id}</td>
                                <td>{ev.name}</td>
                                <td>{ev.start_time}</td>
                                <td>{ev.place?.name}</td>
                                <td>
                                    <button onClick={() => { setEditingEvent(ev); setShowModal(true); }}>
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {showModal && editingEvent &&
                    ReactDOM.createPortal(
                        <div className="modal-overlay">
                            <div className="modal">
                                <h3>Edit Event</h3>
                                <form onSubmit={handleSave} className="modal-form admin-form">

                                    <div className="form-group">
                                        <label>Name</label>
                                        <input
                                            type="text"
                                            value={editingEvent.name || ""}
                                            onChange={e => updateField("name", e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea
                                            value={editingEvent.description || ""}
                                            onChange={e => updateField("description", e.target.value)}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Event Type *</label>
                                        <input
                                            type="text"
                                            value={editingEvent.type || ""}
                                            onChange={e => updateField("type", e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Start Time *</label>
                                        <input
                                            type="datetime-local"
                                            value={toInputDate(editingEvent.start_time)}
                                            onChange={e => updateField("start_time", fromInputDate(e.target.value))}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>End Time</label>
                                        <input
                                            type="datetime-local"
                                            value={toInputDate(editingEvent.end_time)}
                                            onChange={e => updateField("end_time", fromInputDate(e.target.value))}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Location Name</label>
                                        <input
                                            type="text"
                                            value={editingEvent.place?.name || ""}
                                            onChange={e => updateNested("place", "name", e.target.value)}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Ticket Link</label>
                                        <input
                                            type="text"
                                            value={editingEvent.ticket_uri || ""}
                                            onChange={e => updateField("ticket_uri", e.target.value)}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Cover Image URL</label>
                                        <input
                                            type="text"
                                            value={editingEvent.cover?.source || ""}
                                            onChange={e => updateNested("cover", "source", e.target.value)}
                                        />
                                    </div>

                                    <div className="modal-buttons">
                                        <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>
                                            Cancel
                                        </button>
                                        <button type="submit" className="save-btn" disabled={saving}>
                                            {saving ? "Saving..." : "Save Changes"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>,
                        document.body
                    )
                }
            </div>
        </div>
    );
};

export default CalendarUpdate;